import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronLeft, FiChevronRight, FiPhone, FiSend, FiX } from 'react-icons/fi';
import { fullUrl } from '../api';
import { useLanguage } from '../i18n';

/** First popup a customer sees when opening the shop.
 *
 *  Owner-managed (Dashboard > Promotion Popup): image + title + description +
 *  contact. Several active promotions get ‹ › navigation, dots and Close. */
export default function PromotionPopup({ promotions = [], shopId }) {
  const { t } = useLanguage();
  const list = useMemo(() => (promotions || []).filter((p) => p.active !== false), [promotions]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const close = useCallback(() => setOpen(false), []);

  // Show on EVERY page load -> refreshing the page opens the popup again.
  // (No sessionStorage flag: the popup is meant to greet every new visit.)
  useEffect(() => {
    if (!shopId || !list.length) return undefined;
    const timer = setTimeout(() => {
      setIdx(0);
      setOpen(true);
    }, 400);
    return () => clearTimeout(timer);
  }, [shopId, list.length]);

  // Keyboard navigation + body scroll lock while the popup is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + list.length) % list.length);
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % list.length);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, list.length, close]);

  if (!open || !list.length) return null;

  const p = list[Math.min(idx, list.length - 1)];
  const many = list.length > 1;
  const go = (step) => setIdx((i) => (i + step + list.length) % list.length);

  return createPortal(
    <div className="fixed inset-0 z-[998] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm anim-fade-in" onClick={close} />

      <div className="relative m-auto anim-modal flex flex-col w-full max-w-lg max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 shadow-2xl">
        <button
          onClick={close}
          aria-label={t('close')}
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm active:scale-90"
        >
          <FiX className="w-5 h-5" />
        </button>

        {many && (
          <>
            <button
              onClick={() => go(-1)}
              aria-label={t('prev')}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm active:scale-90"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label={t('next')}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm active:scale-90"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        <div className="overflow-y-auto no-scrollbar">
          {p.image ? (
            <img src={fullUrl(p.image)} alt="" className="w-full aspect-video object-cover" />
          ) : (
            <div className="w-full aspect-video flex items-center justify-center text-white"
              style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))' }}>
              <span className="text-sm font-semibold tracking-wide uppercase opacity-90">{t('promotionOffer')}</span>
            </div>
          )}

          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--primary)' }}>
              {t('promotion')}
              {many && <span className="text-gray-400 font-medium">{idx + 1} / {list.length}</span>}
            </div>
            {p.title && <h2 className="text-xl font-bold leading-snug">{p.title}</h2>}
            {p.description && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-gray-600 dark:text-gray-300">{p.description}</p>
            )}
            {p.contact && (
              <div className="flex items-start gap-2 text-sm font-medium bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2">
                <FiPhone className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--primary)' }} />
                <span className="break-all whitespace-pre-wrap">{p.contact}</span>
              </div>
            )}
            {p.link && (
              <a
                href={p.link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-bold text-white shadow-sm active:scale-[.99]"
                style={{ background: 'var(--primary)' }}
              >
                <FiSend className="w-4 h-4" /> {t('shopNow')}
              </a>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {many && list.map((item, i) => (
              <button
                key={item.id || i}
                onClick={() => setIdx(i)}
                aria-label={`${i + 1}`}
                className="w-2.5 h-2.5 rounded-full transition"
                style={{ background: i === idx ? 'var(--primary)' : 'rgba(0,0,0,.18)' }}
              />
            ))}
          </div>
          <button
            onClick={close}
            className="px-5 py-2.5 rounded-xl font-bold border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-[.98]"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

